import React from 'react';

import styles from './styles.module.css';

import type { CookieBannerConfig } from '@/shared/api/legal';

export interface PrivacyChoicesFormProps {
  categories: CookieBannerConfig['categories'];
  analytics: boolean;
  sale: boolean;
  gpc: boolean;
  onToggleAnalytics: () => void;
  onToggleSale: () => void;
}

/**
 * The necessary/analytics/sale toggle rows, shared between the CookieBanner's
 * "Manage preferences" panel and the standalone "Your Privacy Choices" page so
 * the two surfaces can never drift apart on copy or structure. Presentational
 * only — callers own the surrounding layout, the Submit/Save button, and how
 * the resulting choice gets persisted.
 */
const PrivacyChoicesForm: React.FC<PrivacyChoicesFormProps> = ({
  categories,
  analytics,
  sale,
  gpc,
  onToggleAnalytics,
  onToggleSale
}) => (
  <div className={styles.list}>
    <Category
      title={categories.necessary.title}
      description={categories.necessary.description}
      checked={true}
      locked={true}
    />
    <Category
      title={categories.analytics.title}
      description={categories.analytics.description}
      checked={analytics}
      onToggle={onToggleAnalytics}
    />
    <Category
      title={categories.sale.title}
      description={categories.sale.description}
      checked={sale}
      locked={gpc}
      onToggle={onToggleSale}
    />
  </div>
);

export const Category: React.FC<{
  title: string;
  description: string;
  checked: boolean;
  locked?: boolean;
  onToggle?: () => void;
}> = ({ title, description, checked, locked = false, onToggle }) => (
  <div className={styles.category}>
    <div className={styles.categoryHead}>
      <span className={styles.categoryTitle}>{title}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={title}
        className={styles.switch}
        data-on={checked}
        data-locked={locked}
        onClick={locked ? undefined : onToggle}
        disabled={locked}
      >
        <span className={styles.switchKnob} />
      </button>
    </div>
    <p className={styles.categoryDesc}>{description}</p>
  </div>
);

export default PrivacyChoicesForm;
